import { Suspense } from 'react';
import { Skeleton } from '@worldcoo/ui/components/skeleton';
import { Button, buttonVariants } from '@worldcoo/ui/components/button';
import { isNotFound, Link } from '@tanstack/react-router';
import { usePortalContext } from '@/lib/use-portal-context';
import { useTranslation } from '@/lib/i18n';
import { CampaignStatistics } from './statistics-campaign';
import { DivWithPrimaryColorVariable } from './div-with-primary-color-variable';
import { cn, heroImageSize } from '@worldcoo/ui/lib/utils';
import { sanitizeHTML } from '@worldcoo/ui/lib/sanitize-html';
import { Image } from '../../-components/image';
import { NotTranslatedInfo } from './not-translated-info';
import { usePortalCampaignQuery } from '@/lib/queries';

function extractYouTubeVideoId(url: string): string | null {
	try {
		const parsed = new URL(url);
		if (parsed.hostname === 'youtu.be') return parsed.pathname.slice(1);
		if (parsed.hostname.includes('youtube.com')) return parsed.searchParams.get('v');
	} catch {
		// invalid URL
	}
	return null;
}

function YouTubeEmbed(props: { url: string; title?: string }) {
	const videoId = extractYouTubeVideoId(props.url);
	if (!videoId) return null;

	return (
		<div className="flex h-full items-center justify-center">
			{/* oxlint-disable-next-line react/iframe-missing-sandbox */}
			<iframe
				src={`https://www.youtube-nocookie.com/embed/${videoId}?rel=0&controls=0`}
				title={props.title ?? 'Campaign video'}
				allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
				referrerPolicy="strict-origin-when-cross-origin"
				allowFullScreen
				className={cn('aspect-video w-full rounded-2xl')}
			/>
		</div>
	);
}

export function CampaignHero(props: { campaignId: string }) {
	return (
		<Suspense
			fallback={
				<div className="flex flex-col lg:flex-row">
					<section className="flex w-full flex-col gap-6 px-6 pb-10 lg:w-1/2">
						<Skeleton className="bg-secondary h-9 w-32" />

						<div className="flex flex-col gap-1">
							<Skeleton className="bg-secondary h-6 w-lg" />
							<Skeleton className="bg-secondary h-6 w-md" />
							<Skeleton className="bg-secondary h-6 w-lg" />
							<br />
							<Skeleton className="bg-secondary h-6 w-lg" />
							<Skeleton className="bg-secondary h-6 w-md" />
							<br />
							<Skeleton className="bg-secondary h-6 w-lg" />
							<Skeleton className="bg-secondary h-6 w-md" />
							<Skeleton className="bg-secondary h-6 w-sm" />
						</div>

						<Skeleton className="bg-secondary h-14 w-48" />
					</section>

					<div className="w-full p-10 pb-14 lg:w-1/2">
						<Skeleton className={cn('bg-secondary h-full w-full rounded-2xl', heroImageSize.className)} />
					</div>
				</div>
			}
		>
			<SuspensedCampaignHero campaignId={props.campaignId} />
		</Suspense>
	);
}

function SuspensedCampaignHero(props: { campaignId: string }) {
	const { portalId } = usePortalContext();
	const { i18n } = useTranslation();
	const campaign = usePortalCampaignQuery(props.campaignId);

	if (!portalId || isNotFound(campaign)) return null;

	return (
		<DivWithPrimaryColorVariable className="bg-background">
			{campaign.data.textsNotFound && <NotTranslatedInfo className="mx-6 mb-6" />}

			<div className="flex flex-col lg:flex-row">
				<section className="flex w-full flex-col gap-6 px-6 pb-10 lg:w-1/2">
					<CampaignStatistics campaignId={props.campaignId} />

					{campaign.data.texts.corporateTitle && (
						<h1
							className="text-4xl font-medium"
							dangerouslySetInnerHTML={{ __html: sanitizeHTML(campaign.data.texts.corporateTitle) }}
						/>
					)}

					{campaign.data.texts.resumeShort && (
						<div
							className="unreset-tailwind"
							dangerouslySetInnerHTML={{
								__html: sanitizeHTML(campaign.data.texts.resumeShort),
							}}
						/>
					)}

					{campaign.data.finished ? (
						<Button size="2xl" className="hidden sm:w-fit lg:flex" disabled>
							{i18n.portal.campaignFunded}
						</Button>
					) : (
						<Link
							to="/$portalId/$campaignId/donate"
							params={{ portalId, campaignId: campaign.data.id }}
							search={(prev) => prev}
							className={cn(buttonVariants({ size: '2xl' }), 'hidden sm:w-fit lg:flex')}
						>
							{i18n.actions.makeDonation}
						</Link>
					)}
				</section>

				<div className="w-full px-10 pb-14 lg:w-1/2">
					{campaign.data.heroVideoUrl ? (
						<YouTubeEmbed url={campaign.data.heroVideoUrl} title={campaign.data.heroVideoAlt} />
					) : (
						<Image
							src={campaign.data.media?.location}
							height={heroImageSize.height}
							aspectRatio={heroImageSize.width / heroImageSize.height}
							className={cn('w-full rounded-2xl object-cover transition-all', heroImageSize.className)}
							alt={campaign.data.heroImageAlt ?? 'Campaign hero image'}
							fetchPriority="high"
						/>
					)}
				</div>
			</div>
		</DivWithPrimaryColorVariable>
	);
}
